import { useEffect, useRef, useState } from "react"

interface UseRotatingPlaceholderOptions {
  // Strings to cycle through. The hook does NOT change identity-tracking on
  // array reference, so callers should memoize or rely on the stable join.
  prompts: string[]
  // When false (or list is empty), returns `fallback` and stops animating.
  // Flip to false on first user interaction to freeze rotation.
  enabled: boolean
  // Static placeholder shown when not animating (reduced motion, no prompts,
  // or after the user starts typing).
  fallback: string
  // Per-character typing/deleting interval in ms. Default 35ms.
  charDelayMs?: number
  // How long to hold the fully-typed prompt before deleting. Default 1800ms.
  pauseMs?: number
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

/**
 * Cycles a textarea/input placeholder through a list of prompts using a
 * passive typewriter effect. Caller is expected to wire `enabled=false` once
 * the user focuses or types so rotation freezes — the hook itself doesn't
 * observe input events.
 */
export function useRotatingPlaceholder({
  prompts,
  enabled,
  fallback,
  charDelayMs = 35,
  pauseMs = 1800,
}: UseRotatingPlaceholderOptions): string {
  const [display, setDisplay] = useState<string>(fallback)
  // Stable separator () avoids collisions with any character a prompt could contain.
  const promptsKey = prompts.join("")
  // Read fallback through a ref so a new string identity from the parent's
  // re-render (e.g. t() returning a fresh value) doesn't tear down the
  // typewriter mid-cycle. The effect only re-runs on enabled / promptsKey.
  const fallbackRef = useRef(fallback)
  useEffect(() => {
    fallbackRef.current = fallback
  }, [fallback])

  useEffect(() => {
    if (!enabled || prompts.length === 0 || prefersReducedMotion()) {
      setDisplay(fallbackRef.current)
      return
    }

    let promptIdx = 0
    let charIdx = 0
    let mode: "typing" | "deleting" = "typing"
    let intervalId: ReturnType<typeof setInterval> | null = null
    let pauseTimeout: ReturnType<typeof setTimeout> | null = null
    let cancelled = false

    const start = () => {
      if (cancelled) return
      intervalId = setInterval(tick, charDelayMs)
    }

    const tick = () => {
      if (cancelled) return
      const cur = prompts[promptIdx % prompts.length]
      if (mode === "typing") {
        charIdx = Math.min(cur.length, charIdx + 1)
        setDisplay(cur.slice(0, charIdx))
        if (charIdx >= cur.length) {
          if (intervalId) {
            clearInterval(intervalId)
            intervalId = null
          }
          pauseTimeout = setTimeout(() => {
            if (cancelled) return
            mode = "deleting"
            start()
          }, pauseMs)
        }
      } else {
        charIdx = Math.max(0, charIdx - 2)
        setDisplay(cur.slice(0, charIdx))
        if (charIdx === 0) {
          promptIdx = (promptIdx + 1) % prompts.length
          mode = "typing"
        }
      }
    }

    setDisplay("")
    start()

    return () => {
      cancelled = true
      if (intervalId) clearInterval(intervalId)
      if (pauseTimeout) clearTimeout(pauseTimeout)
    }
  }, [enabled, promptsKey, charDelayMs, pauseMs])

  return display
}
